import { useFrame } from '@react-three/fiber'
import { useMemo, useRef } from 'react'
import { AdditiveBlending, DoubleSide, Mesh, Quaternion, ShaderMaterial, type Group } from 'three'

const vertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`
const fragmentShader = `
  uniform float tempo;
  uniform float opacidade;
  varying vec2 vUv;
  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x), mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), f.x), f.y);
  }
  void main() {
    float y = vUv.y;
    float turbulencia = noise(vec2(vUv.x * 8., y * 7. - tempo * 2.5));
    float x = abs(vUv.x - .5 + sin(y * 10. - tempo * 4.) * .055 * y);
    float largura = (.32 * pow(1. - y, .7) + .018) * (.75 + .5 * turbulencia);
    float chama = 1. - smoothstep(largura * .45, largura, x);
    float borda = smoothstep(0., .045, y) * (1. - smoothstep(.86, 1., y));
    float nucleo = (1. - smoothstep(0., largura * .55, x)) * (1. - y);
    vec3 cor = mix(vec3(1., .14, .012), vec3(1., .64, .07), chama);
    cor = mix(cor, vec3(1., .94, .64), nucleo);
    float alpha = chama * borda * (.72 + .25 * turbulencia);
    if (alpha < .015) discard;
    gl_FragColor = vec4(cor, alpha * opacidade);
  }
`

/** Chamas translúcidas em planos cruzados; o núcleo nasce exatamente na origem. */
export function ChamasMesa3D({ jato = false }: { jato?: boolean }) {
  const chamas = useRef<Group>(null)
  const fumaca = useRef<Group>(null)
  const inv = useMemo(() => new Quaternion(), [])
  useFrame(({ clock }) => {
    const t = clock.elapsedTime
    chamas.current?.children.forEach(o => {
      if (!(o instanceof Mesh) || !(o.material instanceof ShaderMaterial)) return
      o.material.uniforms.tempo!.value = t
      o.material.uniforms.opacidade!.value = o.material.opacity
    })
    if (chamas.current) chamas.current.scale.y = 1 + Math.sin(t * 7) * .045
    if (fumaca.current?.parent) {
      fumaca.current.parent.getWorldQuaternion(inv)
      fumaca.current.quaternion.copy(inv.invert())
    }
    if (fumaca.current) fumaca.current.children.forEach((p, i) => {
      const fase = (t * .18 + i / 9) % 1
      p.position.set(.15 * Math.sin(i * 2 + fase * 3), fase * 3.8, -.1 + fase * .4)
      p.scale.setScalar(.15 + fase * .55)
    })
  })
  return (
    <group name={jato ? 'chama-valvula' : 'fogo-volumetrico'}>
      <group ref={chamas} scale={[jato ? .75 : 1.6, 1, jato ? .75 : 1.6]}>
        {[0, Math.PI / 3, Math.PI * 2 / 3].map((angulo) => (
          <mesh key={angulo} position={[0, 1.25, 0]} rotation={[0, angulo, 0]}>
            <planeGeometry args={[1.5, 2.5]} />
            <shaderMaterial uniforms={{ tempo: { value: 0 }, opacidade: { value: 1 } }} vertexShader={vertexShader} fragmentShader={fragmentShader} transparent depthWrite={false} side={DoubleSide} blending={AdditiveBlending} toneMapped={false} />
          </mesh>
        ))}
      </group>
      <group ref={fumaca} position={[0, 1.7, 0]} name="fumaca-ascendente">
        {Array.from({ length: 9 }, (_, i) => (
          <mesh key={i}>
            <sphereGeometry args={[1, 12, 8]} />
            <meshStandardMaterial color={i % 2 ? '#555c60' : '#72777a'} transparent opacity={.22} depthWrite={false} roughness={1} />
          </mesh>
        ))}
      </group>
    </group>
  )
}
